import { Link, useLocation } from 'react-router-dom';
import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { LayoutDashboard, Users, Clock, Calendar, FileText, LogOut, UserCog, Star, Settings as SettingsIcon, BellRing, PanelLeftClose, PanelLeftOpen, X } from 'lucide-react';
import { createTranslator } from '@/i18n';
import { useLanguage } from '@/context/LanguageContext';

export default function Layout({ user, children, onLogout }) {
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [sidebarMode, setSidebarMode] = useState(() => {
    const saved = localStorage.getItem('sidebar_mode');
    if (saved === 'full' || saved === 'compact' || saved === 'hidden') return saved;
    return localStorage.getItem('sidebar_hidden') === '1' ? 'hidden' : 'full';
  });
  const changeMode = (mode) => {
    localStorage.setItem('sidebar_mode', mode);
    setSidebarMode(mode);
  };
  const compact = sidebarMode === 'compact';
  const { language, setLanguage } = useLanguage();
  const t = createTranslator(language || 'is');

  const navItems = [
    { path: '/dashboard', label: t('nav_dashboard'), icon: LayoutDashboard },
    ...(user.role === 'admin'
      ? [
          { path: '/employees', label: t('nav_employees'), icon: Users },
          { path: '/users', label: t('nav_users'), icon: UserCog },
        ]
      : []),
    { path: '/timesheet', label: t('nav_timesheet'), icon: Clock },
    { path: '/calendar', label: t('nav_calendar'), icon: Calendar },
    { path: '/leave', label: t('nav_leave'), icon: FileText },
    ...(user.role === 'admin'
      ? [
          { path: '/attendance', label: 'Mæting', icon: BellRing },
          { path: '/holidays', label: 'Helgidagar', icon: Star },
          { path: '/settings', label: t('nav_settings'), icon: SettingsIcon },
        ]
      : []),
  ];

  return (
    <div className="flex h-screen">
      {/* Mobile top bar */}
      <div className="md:hidden fixed top-0 left-0 right-0 z-20 flex items-center justify-between bg-white shadow px-4 py-3">
        <div>
          <h2
            className="text-lg font-bold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent"
            style={{ fontFamily: 'Work Sans' }}
          >
            Stimpilklukka
          </h2>
          <p className="text-xs text-gray-500">{user.full_name}</p>
        </div>
        <Button
          variant="outline"
          size="icon"
          onClick={() => setSidebarOpen((prev) => !prev)}
          className="ml-2"
          aria-label="Opna valmynd"
        >
          <span className="sr-only">Toggle navigation</span>
          <div className="space-y-1">
            <span className="block h-0.5 w-5 bg-gray-800" />
            <span className="block h-0.5 w-5 bg-gray-800" />
            <span className="block h-0.5 w-5 bg-gray-800" />
          </div>
        </Button>
      </div>

      {/* Sýna-valmynd hnappur (desktop, þegar valmynd er falin) */}
      {sidebarMode === 'hidden' && (
        <Button
          variant="outline"
          size="icon"
          onClick={() => changeMode('full')}
          className="hidden md:flex fixed top-4 left-4 z-30 bg-white shadow-md"
          aria-label="Sýna valmynd"
          title="Sýna valmynd"
          data-testid="sidebar-show-btn"
        >
          <PanelLeftOpen className="h-5 w-5" />
        </Button>
      )}

      {/* Sidebar */}
      <div
        className={`fixed md:static inset-y-0 left-0 z-30 w-64 bg-white shadow-lg flex flex-col transform md:transform-none transition-all duration-200 ${
          sidebarOpen ? 'translate-x-0' : '-translate-x-full md:translate-x-0'
        } ${sidebarMode === 'hidden' ? 'md:hidden' : ''} ${compact ? 'md:w-16' : ''}`}
      >
        <div className={`border-b hidden md:block relative ${compact ? 'p-2' : 'p-6'}`}>
          {compact ? (
            <div className="flex flex-col items-center gap-1">
              <Button
                variant="ghost"
                size="icon"
                onClick={() => changeMode('full')}
                className="text-gray-500 hover:text-gray-800"
                aria-label="Stækka valmynd"
                title="Stækka valmynd"
                data-testid="sidebar-expand-btn"
              >
                <PanelLeftOpen className="h-5 w-5" />
              </Button>
              <Button
                variant="ghost"
                size="icon"
                onClick={() => changeMode('hidden')}
                className="text-gray-500 hover:text-gray-800"
                aria-label="Fela valmynd"
                title="Fela valmynd"
                data-testid="sidebar-hide-btn"
              >
                <X className="h-5 w-5" />
              </Button>
            </div>
          ) : (
            <>
              <Button
                variant="ghost"
                size="icon"
                onClick={() => changeMode('compact')}
                className="absolute top-3 right-3 text-gray-500 hover:text-gray-800"
                aria-label="Minnka valmynd"
                title="Minnka valmynd (bara tákn)"
                data-testid="sidebar-compact-btn"
              >
                <PanelLeftClose className="h-5 w-5" />
              </Button>
              <h2
                className="text-2xl font-bold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent"
                style={{ fontFamily: 'Work Sans' }}
              >
                Stimpilklukka
              </h2>
              <p className="text-sm text-gray-600 mt-1">{user.full_name}</p>
              <p className="text-xs text-gray-500">{user.role === 'admin' ? 'Stjórnandi' : 'Starfsmaður'}</p>
              <div className="mt-3 flex items-center gap-2">
                <span className="text-xs text-gray-500">Language:</span>
                <Button
                  variant={language === 'is' ? 'default' : 'outline'}
                  size="xs"
                  onClick={() => setLanguage('is')}
                  data-testid="lang-is-header"
                >
                  IS
                </Button>
                <Button
                  variant={language === 'en' ? 'default' : 'outline'}
                  size="xs"
                  onClick={() => setLanguage('en')}
                  data-testid="lang-en-header"
                >
                  EN
                </Button>
              </div>
            </>
          )}
        </div>

        <nav className={`flex-1 p-4 space-y-2 mt-16 md:mt-0 ${compact ? 'md:p-2' : ''}`}>
          {navItems.map((item) => {
            const Icon = item.icon;
            const isActive = location.pathname === item.path;
            return (
              <Link key={item.path} to={item.path} onClick={() => setSidebarOpen(false)}>
                <Button
                  variant={isActive ? 'default' : 'ghost'}
                  title={item.label}
                  className={`w-full justify-start ${compact ? 'md:justify-center md:px-2' : ''} ${
                    isActive ? 'bg-gradient-to-r from-blue-500 to-purple-600' : ''
                  }`}
                  data-testid={`nav-${item.path.slice(1)}`}
                >
                  <Icon className={`h-4 w-4 mr-2 ${compact ? 'md:mr-0' : ''}`} />
                  <span className={compact ? 'md:hidden' : ''}>{item.label}</span>
                </Button>
              </Link>
            );
          })}
        </nav>

        <div className={`border-t flex items-center justify-between gap-2 p-4 ${compact ? 'md:p-2 md:justify-center' : ''}`}>
          <div className={`flex items-center gap-2 ${compact ? 'md:hidden' : ''}`}>
            <span className="text-xs text-gray-500">Language:</span>
            <Button
              variant={language === 'is' ? 'default' : 'outline'}
              size="xs"
              onClick={() => setLanguage('is')}
              data-testid="lang-is-footer"
            >
              IS
            </Button>
            <Button
              variant={language === 'en' ? 'default' : 'outline'}
              size="xs"
              onClick={() => setLanguage('en')}
              data-testid="lang-en-footer"
            >
              EN
            </Button>
          </div>
          <Button
            onClick={onLogout}
            variant="outline"
            title={t('logout')}
            className={`justify-start text-red-600 hover:bg-red-50 ${compact ? 'md:px-2' : ''}`}
            data-testid="logout-button"
          >
            <LogOut className={`h-4 w-4 mr-2 ${compact ? 'md:mr-0' : ''}`} />
            <span className={compact ? 'md:hidden' : ''}>{t('logout')}</span>
          </Button>
        </div>
      </div>

      {/* Main content */}
      <div className="flex-1 overflow-auto pt-16 md:pt-0">
        <div className="px-4 py-6 sm:px-6 sm:py-8 max-w-4xl lg:max-w-6xl mx-auto">{children}</div>
      </div>
    </div>
  );
}
